import _ from 'lodash'
import {
  DashboardActivityResourceType,
  Goal,
  ImpactDashboardActivity,
  User,
} from 'Generated/graphql-types'
import {
  CheckListItemWithGoalInfo,
  FilterArgs,
  TabFilterKeyEnum,
} from './types'

export const TASK_DATE_FORMAT = 'YYYY-MM-DD'
export const COMMENT_DATE_FORMAT = 'dddd, MMMM D, YYYY h:mm A'
export const ISO_8601_DATE_FORMAT = 'YYYY-MM-DDTHH:mm:ss[Z]'
export const PRIORTIES_FILTER_TRIGGER = 'prioritiesOnly'

export const filterTasks = (
  tasks: CheckListItemWithGoalInfo[],
  filters: Record<FilterArgs['filterType'], FilterArgs['value']>
) => {
  const { taskOwnerId } = filters
  const categories = filters.categories as string[]
  const goalTitleValues = filters.goalTitleValues as string[]

  return tasks.filter((t) => {
    if (taskOwnerId) {
      if (taskOwnerId === 'unassigned' && t.owner) return false // Unassigned tasks only
      if (
        taskOwnerId !== 'unassigned' &&
        t.owner?.id.toString() !== taskOwnerId
      )
        return false // Match task owner
    }
    if (
      categories.length &&
      t.goal.category &&
      !categories.includes(t.goal.category)
    )
      return false // Match task's parent goal category
    if (goalTitleValues.length && !goalTitleValues.includes(t.goal.title))
      return false
    return true
  })
}

export const getTasksFromGoals = (goals: Goal[]) => {
  const allTasks: CheckListItemWithGoalInfo[] = []
  if (goals?.length) {
    goals.forEach((goal) => {
      goal?.ChecklistItem.forEach((task) => {
        allTasks.push({ ...task, goal })
      })
    })
  }
  return allTasks
}

export const getTasksToDisplay = ({
  activeTab,
  completedTasks,
  incompleteTasks,
  overdueTasks,
  unassignedTasks,
  totalTasks,
}: {
  activeTab: TabFilterKeyEnum
  completedTasks: CheckListItemWithGoalInfo[]
  incompleteTasks: CheckListItemWithGoalInfo[]
  overdueTasks: CheckListItemWithGoalInfo[]
  unassignedTasks: CheckListItemWithGoalInfo[]
  totalTasks: CheckListItemWithGoalInfo[]
}) => {
  switch (activeTab) {
    case 'completed':
      return completedTasks
    case 'todo':
      return incompleteTasks
    case 'pastDue':
      return overdueTasks
    case 'unassigned':
      return unassignedTasks
    default:
      return totalTasks
  }
}

export const getSortedTasks = ({
  sortKey,
  sortOrder,
  tasksToDisplay,
}: {
  sortKey: string | null
  sortOrder: 'asc' | 'desc'
  tasksToDisplay: CheckListItemWithGoalInfo[]
}) =>
  _.orderBy(
    tasksToDisplay,
    (task: Record<string, 'title' | 'category' | User | Goal>) => {
      if (sortKey === null) return 0
      if (sortKey === 'owner') {
        const taskOwner = task.owner as User
        return `${taskOwner?.firstName} ${taskOwner?.lastName}`
      }
      if (sortKey === 'goalTitle') {
        const goal = task.goal as Goal
        return goal.title
      }
      return task[sortKey]
    },
    [sortOrder]
  ) as CheckListItemWithGoalInfo[]

export const getSortedUserResources = ({
  sortKey,
  sortOrder,
  resourcesToDisplay,
}: {
  sortKey: string | null
  sortOrder: 'asc' | 'desc'
  resourcesToDisplay: (ImpactDashboardActivity & {
    isAttachedToGoal: boolean
  })[]
}) =>
  _.orderBy(
    resourcesToDisplay,
    (activity: ImpactDashboardActivity & { isAttachedToGoal: boolean }) => {
      if (sortKey === 'userFullName') return activity.userFullName
      if (sortKey === 'attachedToGoal')
        return activity.isAttachedToGoal ? 'Yes' : 'No'
      if (sortKey === 'category') return activity.resource.nonprofitTopic
      if (sortKey === 'title') return activity.resource.title
      if (sortKey === 'type') return activity.resource.type
      if (sortKey === 'userStatus') return activity.userStatus
      return 0
    },
    [sortOrder]
  ) as (ImpactDashboardActivity & { isAttachedToGoal: boolean })[]

export const getResourceCounts = (
  userResourceActivity: ImpactDashboardActivity[]
) => {
  const uniqueResourceMap: Record<string, boolean> = {}
  const resourcesViewedByCategory: Record<string, number> = {}
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { Article, Course, Template } = DashboardActivityResourceType
  let totalResourcesViewed = 0
  let coursesViewed = 0
  let templatesViewed = 0
  let articlesViewed = 0

  userResourceActivity.forEach((a) => {
    if (!uniqueResourceMap[a.resource.id]) {
      uniqueResourceMap[a.resource.id] = true
      totalResourcesViewed += 1
      if (a.resource.type === Article) articlesViewed += 1
      if (a.resource.type === Course) coursesViewed += 1
      if (a.resource.type === Template) templatesViewed += 1
      resourcesViewedByCategory[a.resource.nonprofitTopic] =
        (resourcesViewedByCategory[a.resource.nonprofitTopic] || 0) + 1
    }
  })

  return {
    articlesViewed,
    coursesViewed,
    resourcesViewedByCategory,
    templatesViewed,
    totalResourcesViewed,
  }
}

export const truncateText = (text: string, maxLength: number) => {
  if (text.length <= maxLength) return text
  return `${text.slice(0, maxLength)}...`
}
